import React, { useState, useEffect, useLayoutEffect } from "react";
import { CSSTransition } from "react-transition-group";
import styles from "./ServicesHeader.module.scss";
import sliderStyles from "./Slider.module.scss";
import { NavLink, Link } from 'react-router-dom';
import { TransitionChevron } from "ui-components-rollup/dist/Widgets"
import _ from "lodash";
import classNames from "classnames";
import { useDispatch, useSelector } from "react-redux";
import SVG from "react-inlinesvg";
import { ClickAway } from "ui-components-rollup/dist/Functional";
import { ReactComponent as PinIcon } from "ui-components-rollup/src/images/monochrome-icons/ic-pin.svg";
import {slideTopToBottomClassNames} from "ui-components-rollup/dist/animations"

const ServiceEntry = ({service: {label, description, to}, logo, isPinned, closeSlider, keyName}) => {
    const dispatch = useDispatch();

    return <Link to={to} onClick={closeSlider} className={sliderStyles['service-entry']}>
        <SVG src={logo} className={sliderStyles['service-icon']}/>
        <div>
            <div className={sliderStyles['service-label']}>{label}</div>
            <div className={sliderStyles['service-description']}>{description}</div>
        </div>
        <button className={classNames(sliderStyles['pin-button'], {[sliderStyles['pin-button-pinned']]: isPinned})} onClick={(e) => {
            if(keyName !== "working-environments") {
                dispatch({
                    type: "MENU:TOGGLE-SERVICE-PIN",
                    payload: {
                        pinned: !isPinned,
                        service: keyName
                    }
                });
            }
            e.preventDefault();
            e.stopPropagation();
        }}><PinIcon/></button>
    </Link>
};

const ServicesSlider = React.forwardRef(({closeSlider}, ref) => {
    const resources = useSelector(state => state.menu.resources);
    const services = useSelector(state => state.menu.services);

    return <div ref={ref} className={sliderStyles['slider']}>
        <ClickAway ignoreParentClick={true} listener={closeSlider}/>
        <section>
            <div className={sliderStyles['section-title']}>Resources</div>
            <div className={sliderStyles['section-grid']}>
                {resources.map(resource => {
                    return <ServiceEntry keyName={resource.key} closeSlider={closeSlider} {...resource}/>
                })}
            </div>
        </section>
        <section>
            <div className={sliderStyles['section-title']}>Services</div>
            <div className={sliderStyles['section-grid']}>
                {services.map(service => {
                    return <ServiceEntry keyName={service.key} closeSlider={closeSlider} {...service}/>
                })}
            </div>
        </section>
    </div>
});

const trackCloseSlider = (state) => {
    const isChanged = state.menu.changedPins;
    if(isChanged) {
        return {
            label: _.map(state.menu.pinnedServices, "key").join(", "),
            value: state.menu.pinnedServices.length
        }
    }
};

const ServicesRow = React.forwardRef(({isServicesSliderOpen, setServicesSliderOpen}, triggerRef) => {
    const pinnedServices = useSelector(state => state.menu.pinnedServices);
    const totalCount = useSelector(state => state.menu.totalCount);
    const dispatch = useDispatch();
    const [showCount, setShowCount] = useState(0);
    const rowRef = React.useRef(null);
    useLayoutEffect(() => {
        const update = () => {
            if(rowRef && rowRef.current) {
                const parentRect = document.body.getBoundingClientRect();
                const parentWidth = parentRect.width;

                let lastIndex = 1;
                _.forEachRight(rowRef.current.children, (child, index) => {
                    const childRect = child.getBoundingClientRect();
                    if(childRect.right < parentWidth - 100) {
                        lastIndex = index;
                        return false;
                    }
                });

                setShowCount(Math.min(9, Math.max(1, lastIndex)));
            }
        };

        update();

        const resize = () => {
            setShowCount(pinnedServices.length);
            setTimeout(() => {
                update();
            });
        };

        window.addEventListener("resize", resize);

        return () => {
            window.removeEventListener("resize", resize);
        };
    }, [rowRef, setShowCount, showCount, pinnedServices]);

    useEffect(() => {
        setShowCount(pinnedServices.length);
    }, [pinnedServices]);

    const extraCount = totalCount - showCount;

    return <div ref={rowRef} className={styles['row']} id="row">
        {_.take(pinnedServices, showCount).map(entry => {
            return <NavLink className={styles['link']} activeClassName={styles["active-link"]} key={entry.key} to={entry.service.to}>
                {entry.service.label}
                <div/>
            </NavLink>
        })}
        <button ref={triggerRef} className={classNames(styles['view-all-button'], {[styles['view-all-button-active']]: isServicesSliderOpen})} onClick={() => {
            dispatch({
                type: isServicesSliderOpen ? "MENU:CLOSE-SLIDER" : "MENU:OPEN-SLIDER",
                track: isServicesSliderOpen ? trackCloseSlider : false
            });
        }}>
            All Services{extraCount>0&&` (+${extraCount})`}
            <TransitionChevron isActive={isServicesSliderOpen} className={styles['slider-chevron']}/>
        </button>
    </div>
});

export default () => {
    const isServicesSliderOpen = useSelector(state => state.menu.isServicesSliderOpen);
    const dispatch = useDispatch();
    const nodeRef = React.useRef(null);
    const servicesTriggerRef = React.useRef(null);

    const closeSlider = (e) => {
        if(e.target !== servicesTriggerRef.current) {
            dispatch({
                type: "MENU:CLOSE-SLIDER",
                track: trackCloseSlider
            })
        }
    };

    return <div className={styles['base']}>
        <ServicesRow isServicesSliderOpen={isServicesSliderOpen} ref={servicesTriggerRef}/>
        <CSSTransition nodeRef={nodeRef} in={isServicesSliderOpen} timeout={400} unmountOnExit classNames={slideTopToBottomClassNames}>
            <ServicesSlider ref={nodeRef} closeSlider={closeSlider}/>
        </CSSTransition>
        {isServicesSliderOpen && <div className={sliderStyles['slider-mask']}>
        </div>}
    </div>
};